import { enableChartsExporting, SupportsChartExport, useMultipleChartsExport } from '@ecosystem/export-utils';
import { ExportPngInfo, InsightRendererProps } from '@ecosystem/newron-client-collections';
import { FC, useEffect } from 'react';
import { Provider } from 'react-redux';
import StackedChart, { StackedChartProps } from '../../../data-visualization/stacked-chart/StackedChart';
import { FilterCriteriaParam } from '@models/CommonParam';
import { kpiDropdownOptions } from '@models/PerformanceAnalysisboard';
import { renderLoading } from '@pages/Loading';
import { getStackedChart } from '@services/PerformanceAnalysisboard.service';
import Store from '../../../state/Store';
import {
  constructDummyPeriodForV2YTDAndMATWeekly,
  getIsUsingCollectionLiveData,
} from '@utils/collectionUtils/CollectionUtils';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { constructFactRevenue } from '@utils/currencyUtils/CurrencyUtils';
import useStackedChartSorting from '../../../utils/hook/sorting/UseStackedChartSorting';
import { FilterKeys, PageType, ShareType } from '@utils/pageConfig/Types';
import MultiStackedChartExportProvider from '@hooks/export/export-stacked-chart/context/MultiStackedChartExportProvider';
import { WarningBannerProps } from '../warning-banner/Types';
import CountryWarningBanner from '../warning-banner/country-warning-banner/CountryWarningBanner';
import { PerformanceAnalysisStackedBarChartInsight } from './Model';
import { getCountryOptions } from '@utils/CountryUtils';
import { ViewType } from '@hooks/export/Model';
import PgWarningBanner from '../warning-banner/pg-warning-banner/PgWarningBanner';
import {
  EXPLORE_SEGMENTS_CARD_TITLE,
  EXPLORE_STACK_BAR_CHART_TITLE,
  PERFORMANCE_ANALYSIS_CARD_TITLE,
} from '@pages/Utilities';
import { getExploreSegmentsStackedChart } from '@services/explore/Explore.service';
import { PerformanceAnalysisView, PerformanceAnalysisViewMapping } from '@components/performance-analysis-card/Types';
import { exportChartKey } from '@data-visualization/trends-stack-chart/StackedChartUtilities';
import { convertToExploreTrendsStack } from '@data-visualization/stacked-chart/StackedChartUtils';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { processExportedImages } from '@hooks/export/ExportChart';

enableChartsExporting();
type StackedBarChartInsightRenderer = InsightRendererProps<PerformanceAnalysisStackedBarChartInsight>;

const StackedBarChartInsightContainer: FC<StackedBarChartInsightRenderer> = ({ insight, onLoaded, onExportReady }) => {
  const { sortStackedChartRespData } = useStackedChartSorting();
  const { exportImages } = useExportImages(PageType.EXPLORE);

  const {
    apiParams,
    segmentByFeature,
    showPerformanceWithin,
    selectedGrowthType,
    currencySymbol,
    sortByStackedChart,
    volume,
    collectionDataType,
    insightCardTitle,
  } = insight.data;
  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(apiParams.period, collectionDataType);
      const params: FilterCriteriaParam = {
        ...apiParams,
        period: isUsingCollectionLiveData
          ? constructDummyPeriodForV2YTDAndMATWeekly(apiParams.period, apiParams.periodType)
          : apiParams.period,
        isUsingCollectionLiveData: isUsingCollectionLiveData,
      };
      const response =
        insightCardTitle === EXPLORE_SEGMENTS_CARD_TITLE
          ? await getExploreSegmentsStackedChart(params)
          : await getStackedChart(params);
      return sortStackedChartRespData(response, sortByStackedChart, true);
    },
    onLoaded: onLoaded,
  });
  const kpiHeaderKey = kpiDropdownOptions(volume).find((kpiOption) => kpiOption.value === apiParams.kpi)!.headerKey;

  const multipleChartsExport = useMultipleChartsExport(1);
  const stackedBarChartProps: SupportsChartExport & StackedChartProps = {
    stackedChartRespData: chartData ?? [],
    showPerformanceWithin: showPerformanceWithin,
    selectedGrowthType: selectedGrowthType,
    isLoading: isLoading,
    segmentByFeature: segmentByFeature,
    setSegmentByFeature: () => {},
    featureFilters: [],
    factRevenue: constructFactRevenue(currencySymbol),
    featureSelectorType: {
      featureFilters: [],
      setFeature: () => {},
      isDisabled: true,
      feature: { value: '', label: '', dataType: '', feature: '', filterKey: FilterKeys.SELECT },
    },
    featureHandler: () => {},
    segmentByHandler: () => {},
    isExportModalOpen: true,
    noResultHandler: () => {},
    volume: volume,
    view: ViewType.StackedChart,
    isRestoreFromSavedView: false,
    setIsRestoreFromSavedView: () => {},
    sortByStackedChart: sortByStackedChart,
    setSortByStackedChart: () => {},
    isUsingCollection: true,
    onExportReady: multipleChartsExport.exportCallbacks[0].onExportReady,
    collectionStackedChartHeaderKey: kpiHeaderKey,
    selectedKpi: kpiHeaderKey,
    componentName:
      insightCardTitle === EXPLORE_SEGMENTS_CARD_TITLE
        ? EXPLORE_STACK_BAR_CHART_TITLE
        : `${PERFORMANCE_ANALYSIS_CARD_TITLE} ${
            PerformanceAnalysisViewMapping[PerformanceAnalysisView.StackedBarChart]
          }`,
  };

  useEffect(() => {
    if (!chartData?.length) return;

    const run = async () => {
      const segmentsChartData = convertToExploreTrendsStack({
        StackChartData: chartData,
        selectedKpi: kpiHeaderKey,
        selectedGrowthType,
      });

      const key = exportChartKey(segmentsChartData, ShareType.MarketShare, kpiHeaderKey, true, true);
      const chartImages = exportImages[key];
      if (!chartImages) return;

      const processedImages = await processExportedImages([chartImages]);
      onExportReady({
        exportPng: async (): Promise<ExportPngInfo> => ({ images: processedImages }),
      });
    };

    run();
  }, [chartData, exportImages, kpiHeaderKey, selectedGrowthType, onExportReady]);

  if (isLoading) {
    return renderLoading();
  }

  return <StackedChart {...stackedBarChartProps} />;
};

const StackedBarChartRenderer: FC<StackedBarChartInsightRenderer> = (props) => {
  const {
    apiParams: { clientId, period, periodType, countries, productGroup, productType, comparisonPeriod },
    collectionDataType,
  } = props.insight.data;

  const productGroups = productGroup ?? [];

  const warningBannerProps: WarningBannerProps = {
    clientId: clientId,
    countries: getCountryOptions(countries),
    productGroups,
    period: period,
    periodicity: periodType,
    comparisonPeriod,
    productType,
    collectionDataType,
  };

  if (countries.length > 1) {
    return (
      <Provider store={Store}>
        <MultiStackedChartExportProvider>
          <CountryWarningBanner warningBannerProps={warningBannerProps}>
            <StackedBarChartInsightContainer {...props} />
          </CountryWarningBanner>
        </MultiStackedChartExportProvider>
      </Provider>
    );
  }

  const baseApiParams = {
    ...props.insight.data.apiParams,
    productGroup: productGroups,
  };

  return (
    <Provider store={Store}>
      <MultiStackedChartExportProvider>
        <PgWarningBanner baseApiParams={baseApiParams} collectionDataType={props.insight.dataType}>
          <StackedBarChartInsightContainer {...props} />
        </PgWarningBanner>
      </MultiStackedChartExportProvider>
    </Provider>
  );
};
export default StackedBarChartRenderer;
